import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { authBackend } from "../lib/authBackend";
import { fetchMe } from "../lib/api";

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const from = location.state?.from?.pathname || "/";

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      toast.error("Enter your email and password.");
      return;
    }
    setLoading(true);
    try {
      const { error } = await authBackend.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (error) throw error;

      // Valid credentials don't mean the account is allowed in — /auth/me is
      // the real gate (checks app_users.status === "active"). Check it here,
      // before navigating in, so a blocked account gets a clear message
      // instead of a silent bounce back to /login after the app briefly loads.
      let me;
      try {
        me = await fetchMe();
      } catch (meErr) {
        await authBackend.signOut();
        const detail = meErr?.response?.data?.detail;
        throw new Error(detail || "This account is not active. Contact your administrator.");
      }

      // Super Admin is platform-side config, not a brand workspace — always
      // land there on login regardless of where the redirect-back `from` points.
      navigate(me?.role === "superadmin" ? "/superadmin" : from, { replace: true });
    } catch (err) {
      toast.error(err?.message || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
        <div className="text-center">
          <h1 className="font-work text-xl font-semibold text-slate-900">In Service Drive</h1>
          <p className="text-sm text-slate-500 mt-1">Log in to your account</p>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="login-form">
          <div>
            <label className="uplabel mb-1.5 block">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              autoFocus
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <label className="uplabel mb-1.5 block">Password</label>
            <input
              data-testid="login-password-input"
              type="password"
              className={inputCls}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2.5 text-sm transition-colors disabled:opacity-60"
          >
            {loading ? <><Loader2 className="animate-spin" size={16} /> Logging in…</> : "Log in"}
          </button>
        </form>
        <p className="text-center text-sm text-slate-500">
          Don't have a password yet?{" "}
          <Link to="/signup" className="text-blue-600 hover:underline font-medium">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
